import { useReducer } from 'react';

import { Correctness } from '@showbie-socrative/socrative-utils';
import produce from 'immer';

import { toNumber, toPrecision } from '~shared/utils/number';

interface StudentScoreModalState {
  isModalDisabled: boolean;
  isErrorMessageOpen: boolean;
  formCorrectness: Correctness;
  formScore: number;
}

export enum ActionType {
  DisableModal = 'disableModal',
  EnableModal = 'enableModal',
  OpenError = 'openError',
  CloseError = 'closeError',
  ClearStudentScore = 'clearStudentScore',
  UpdateScore = 'updateScore',
  UpdateCorrectness = 'updateCorrectness',
}

type Action =
  | { type: ActionType.DisableModal }
  | { type: ActionType.EnableModal }
  | { type: ActionType.OpenError }
  | { type: ActionType.CloseError }
  | { type: ActionType.ClearStudentScore }
  | { type: ActionType.UpdateScore; payload: number }
  | { type: ActionType.UpdateCorrectness; payload: Correctness };

export function calculateScore({
  gradeWeight,
  correctness,
  ungradedScore = 0,
}: {
  gradeWeight: number;
  correctness: Correctness;
  ungradedScore?: number;
}) {
  switch (correctness) {
    case Correctness.Correct: {
      return gradeWeight;
    }
    case Correctness.Incorrect: {
      return ungradedScore;
    }
    case Correctness.Partial: {
      return gradeWeight >= 0 ? gradeWeight / 2 : 0;
    }
  }
}

function calculateCorrectness({
  gradingWeight,
  newScore,
}: {
  gradingWeight: number;
  newScore: number;
}) {
  if (newScore === 0) {
    return Correctness.Incorrect;
  }

  if (newScore >= gradingWeight) {
    return Correctness.Correct;
  }

  return Correctness.Partial;
}

export function createStudentScoreModalReducer({
  responseScore,
  responseCorrectness,
  gradingWeight,
  ungradedScore = 0,
}: {
  responseCorrectness: Correctness;
  responseScore: number;
  gradingWeight: number;
  ungradedScore: number;
}) {
  return function studentScoreModalReducer(
    state: StudentScoreModalState,
    action: Action
  ) {
    return produce(state, (nextState) => {
      switch (action.type) {
        case ActionType.DisableModal: {
          nextState.isModalDisabled = true;
          break;
        }
        case ActionType.EnableModal: {
          nextState.isModalDisabled = false;
          break;
        }
        case ActionType.OpenError: {
          nextState.isErrorMessageOpen = true;
          break;
        }
        case ActionType.CloseError: {
          nextState.isErrorMessageOpen = false;
          break;
        }
        case ActionType.ClearStudentScore: {
          nextState.formCorrectness = responseCorrectness;
          nextState.formScore = responseScore;
          break;
        }
        case ActionType.UpdateCorrectness: {
          nextState.formScore = toNumber(
            toPrecision(
              calculateScore({
                gradeWeight: gradingWeight,
                correctness: action.payload,
                ungradedScore: ungradedScore,
              }),
              2
            )
          );
          nextState.formCorrectness = action.payload;
          break;
        }
        case ActionType.UpdateScore: {
          const { payload: newScore } = action;

          nextState.formScore = newScore;
          nextState.formCorrectness = calculateCorrectness({
            gradingWeight: gradingWeight,
            newScore: newScore,
          });
          break;
        }
      }
    });
  };
}

export const useStudentScoreModalReducer = ({
  responseCorrectness,
  initialCorrectness,
  initialScore,
  gradingWeight,
  responseScore,
  ungradedScore = 0,
}: {
  responseCorrectness: Correctness;
  initialCorrectness: Correctness;
  initialScore: number;
  gradingWeight: number;
  ungradedScore: number;
  responseScore: number;
}) =>
  useReducer(
    createStudentScoreModalReducer({
      responseCorrectness,
      gradingWeight,
      ungradedScore,
      responseScore,
    }),
    {
      formScore: initialScore,
      formCorrectness: initialCorrectness,
      isModalDisabled: false,
      isErrorMessageOpen: false,
    }
  );
